import React, { useState } from 'react';
import { X, Delete, Check, RotateCcw, Plus } from 'lucide-react';
import clsx from 'clsx';

export const ChipCalculator = ({ title, onConfirm, onCancel, initialValue = 0 }) => {
    const [displayValue, setDisplayValue] = useState(initialValue > 0 ? String(initialValue) : '');

    const handleDigit = (digit) => {
        if (displayValue.length >= 7) return; // Prevent astronomical numbers
        setDisplayValue(prev => prev + digit);
    };

    const handleBackspace = () => {
        setDisplayValue(prev => prev.slice(0, -1));
    };

    const handleClear = () => {
        setDisplayValue('');
    };

    const handlePreset = (amount) => {
        const current = Number(displayValue) || 0;
        setDisplayValue(String(current + amount));
    };

    const handleConfirm = () => {
        const amount = Number(displayValue) || 0;
        if (amount > 0) {
            onConfirm(amount);
        }
    };

    const DigitButton = ({ value, onClick, className = "" }) => (
        <button
            onClick={() => onClick(value)}
            className={clsx(
                "h-14 flex items-center justify-center bg-slate-800/50 hover:bg-slate-700/80 active:bg-slate-600/90 border border-white/5 rounded-xl transition-all",
                "text-2xl font-black text-slate-100 tabular-nums shadow-lg active:scale-95",
                className
            )}
        >
            {value}
        </button>
    );

    return (
        <div className="bg-slate-900 border border-amber-500/30 rounded-2xl p-4 shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex flex-col gap-4 animate-in fade-in zoom-in duration-200" dir="rtl">
            {/* Header */}
            <div className="flex items-center justify-between px-1">
                <h3 className="text-sm font-black text-amber-500 uppercase tracking-widest">{title}</h3>
                <button onClick={onCancel} className="p-1 hover:bg-white/10 rounded-full transition-colors">
                    <X className="w-4 h-4 text-slate-500" />
                </button>
            </div>

            {/* Display Area */}
            <div className="bg-black/40 border border-white/5 rounded-xl p-4 flex flex-col items-center justify-center min-h-[80px] shadow-inner relative overflow-hidden group">
                <div className="absolute top-1 left-2 text-[8px] text-slate-600 font-black uppercase tracking-tighter opacity-50">Reshima Amount</div>
                <div className="text-4xl font-black text-white tabular-nums tracking-tight flex items-center gap-2" dir="ltr">
                    <span className="text-amber-500 text-2xl font-bold">₪</span>
                    {displayValue || '0'}
                </div>
                {!displayValue && <div className="text-[10px] text-slate-600 font-bold mt-1 uppercase animate-pulse">Enter Amount</div>}
            </div>

            {/* Quick Presets Row */}
            <div className="grid grid-cols-5 gap-1.5 px-0.5">
                {[50, 100, 200, 500, 1000].map((p) => (
                    <button 
                        key={p} 
                        onClick={() => handlePreset(p)}
                        className="py-2 bg-slate-800 hover:bg-slate-700 active:bg-amber-500/20 active:text-amber-400 border border-white/5 rounded-lg text-[10px] font-black text-slate-300 transition-all tabular-nums"
                    >
                        +{p}
                    </button>
                ))}
            </div>

            {/* Numeric Pad Grid */}
            <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                    <DigitButton key={n} value={String(n)} onClick={handleDigit} />
                ))}
                
                <button
                    onClick={handleClear}
                    className="h-14 flex items-center justify-center bg-slate-800/30 hover:bg-rose-500/10 active:bg-rose-500/20 border border-white/5 rounded-xl transition-all group"
                >
                    <RotateCcw className="w-5 h-5 text-slate-500 group-hover:text-rose-400 transition-colors" />
                </button>

                <DigitButton value="0" onClick={handleDigit} />

                <button
                    onClick={handleBackspace}
                    className="h-14 flex items-center justify-center bg-slate-800/30 hover:bg-amber-500/10 active:bg-amber-500/20 border border-white/5 rounded-xl transition-all group"
                >
                    <Delete className="w-5 h-5 text-slate-500 group-hover:text-amber-400 transition-colors" />
                </button>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
                <button
                    onClick={onCancel}
                    className="flex-1 py-4 bg-slate-800 hover:bg-slate-700 text-slate-400 rounded-xl font-black text-xs uppercase tracking-widest transition-all border border-white/5"
                >
                    ביטול
                </button>
                <button
                    onClick={handleConfirm}
                    disabled={!displayValue || Number(displayValue) <= 0}
                    className={clsx(
                        "flex-[2] py-4 rounded-xl font-black text-sm uppercase tracking-widest transition-all shadow-xl flex items-center justify-center gap-2",
                        displayValue && Number(displayValue) > 0 
                            ? "bg-amber-500 hover:bg-amber-400 text-slate-950 shadow-amber-900/40" 
                            : "bg-slate-800 text-slate-600 grayscale cursor-not-allowed opacity-50"
                    )}
                >
                    <Check className="w-4 h-4" />
                    אישור
                </button>
            </div>
        </div>
    );
};
